import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import HttpRoutingService from '../../shared/services/httpRoutingService';
import { API } from '../../shared/apiRoutes';
import {
  rolesResponseProps,
} from '../../components/role/roleModule';
import {
  dataValueProps,
  getAllPosInvitations,
  getPropertyValuesByCode,
  setInivitationProps,
  setPropertyProps,
} from '../../components/invitedStaff/invitedStaffModule';

//type
interface getAllPosInvitationsPayload {
  offset: number;
  limit: number;
  filterData: null | string;
  searchText: null | string;
}

interface initialState {
  loading: boolean;
  posInvitations: {
    value: getAllPosInvitations | null;
    // Cached paginated state
    rows: setInivitationProps[];
    count: number;
    isReached: boolean;         // true when last page returned < limit rows
    filter: string | null;      // JSON fingerprint of { searchText, filterData }
    error: string;
  };
  invitedRoles: rolesResponseProps | null;
  propertyValuesByCode: getPropertyValuesByCode | null;
  deletePosInvitation: null;
  invitationMail: null;
  error: string | null;
}

const initialState: initialState = {
  loading: true,
  posInvitations: {
    value: null,
    rows: [],
    count: 0,
    isReached: false,
    filter: null,
    error: '',
  },
  invitedRoles: null,
  propertyValuesByCode: null,
  deletePosInvitation: null,
  invitationMail: null,
  error: '',
};

export const posInvitations = createAsyncThunk(
  'invitation/getAllPosInvitations',
  ({
    storeId,
    data,
  }: {
    storeId: number;
    data: getAllPosInvitationsPayload | undefined;
  }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllPosInvitations, {
      storeId: storeId,
    });
    return HttpRoutingService?.getMethod<getAllPosInvitations>(url, data);
  },
);
export const invitedRoles = createAsyncThunk(
  'invitation/getAllRoles',
  ({ storeId }: { storeId: number }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllRoles, {
      storeId: storeId,
    });
    return HttpRoutingService?.getMethod<rolesResponseProps>(url);
  },
);
export const propertyValuesByCode = createAsyncThunk(
  'invitation/getPropertyValuesByCode',
  ({ code }: { code: string }) => {
    const url = HttpRoutingService?.replaceUrl(
      API?.PROPERTY_VALUES_BY_CODE,
      {},
    );
    return HttpRoutingService?.getMethod<getPropertyValuesByCode>(
      url,
      { code: code },
      'v1/',
    );
  },
);
export const deletePosInvitation = createAsyncThunk(
  'invitation/deleteInvitation',
  ({ storeId, invitationId }: { storeId: number; invitationId: number }) => {
    const url = HttpRoutingService?.replaceUrl(API?.deleteInvitation, {
      storeId: storeId,
      invitationId: invitationId,
    });
    return HttpRoutingService?.deleteMethod<any>(url, {}, 'v1/');
  },
);
export const invitationMail = createAsyncThunk(
  'invitation/resendInvitationMail',
  ({
    storeId,
    invitationId,
    data,
  }: {
    storeId: number;
    invitationId: number;
    data: dataValueProps;
  }) => {
    const url = HttpRoutingService?.replaceUrl(API?.deleteInvitation, {
      storeId: storeId,
      invitationId: invitationId,
    });
    return HttpRoutingService?.putMethod<any>(url, data, '', 'v1/');
  },
);

export const invitedStaffSlice = createSlice({
  name: 'invited Staff',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(posInvitations?.pending, state => {
        state.loading = true;
      })
      .addCase(posInvitations?.fulfilled, (state, action) => {
        state.loading = false;
        state.posInvitations.value = action.payload;

        const offset = (action.meta.arg.data?.offset ?? 0);
        const limit = (action.meta.arg.data?.limit ?? 10);
        const incoming = action.payload?.invitationsDetails?.rows ?? [];

        if (offset > 0) {
          // Append paginated rows
          state.posInvitations.rows = [...state.posInvitations.rows, ...incoming];
        } else {
          // Fresh fetch — replace rows
          state.posInvitations.rows = incoming;
          state.posInvitations.count = (action.payload as any)?.invitationsDetails?.count ?? 0;
        }

        state.posInvitations.isReached = incoming.length < limit;
        state.posInvitations.filter = JSON.stringify({
          searchText: action.meta.arg.data?.searchText,
          filterData: action.meta.arg.data?.filterData,
        });
      })
      .addCase(posInvitations?.rejected, (state, action) => {
        state.loading = false;
        state.posInvitations.error = action?.error?.message as string;
      })
      .addCase(invitedRoles?.pending, state => {
        state.loading = true;
      })
      .addCase(invitedRoles?.fulfilled, (state, action) => {
        state.loading = false;
        state.invitedRoles = action.payload;
      })
      .addCase(invitedRoles?.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.error?.message as string;
      })
      .addCase(propertyValuesByCode?.pending, state => {
        state.loading = true;
      })
      .addCase(propertyValuesByCode?.fulfilled, (state, action) => {
        state.loading = false;
        state.propertyValuesByCode = action.payload;
      })
      .addCase(propertyValuesByCode?.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.error?.message as string;
      })
      .addCase(deletePosInvitation?.pending, state => {
        state.loading = true;
      })
      .addCase(deletePosInvitation?.fulfilled, (state, action) => {
        state.loading = false;
        state.deletePosInvitation = action.payload;
      })
      .addCase(deletePosInvitation?.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.error?.message as string;
      })
      .addCase(invitationMail?.pending, state => {
        state.loading = true;
      })
      .addCase(invitationMail?.fulfilled, (state, action) => {
        state.loading = false;
        state.invitationMail = action.payload;
      })
      .addCase(invitationMail?.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.error?.message as string;
      });
  },
});

export default invitedStaffSlice?.reducer;
